import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { CheckCircle2, ShieldCheck, ExternalLink } from "lucide-react";

export default function ConsumerSetup() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [acknowledged, setAcknowledged] = useState(false);

  useEffect(() => {
    api.get(`/workflow/consumer-setup/${token}`)
      .then((r) => setInfo(r.data))
      .catch((e) => setError(formatApiError(e)));
  }, [token]);

  const ack = async () => {
    try {
      await api.post(`/workflow/consumer-setup/${token}/engaged`);
      setAcknowledged(true);
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="max-w-md text-center text-sm text-slate-500">
          <div className="font-semibold text-slate-900 mb-1">Setup link unavailable</div>
          <p>{error}</p>
        </div>
      </div>
    );
  }
  if (!info) {
    return <div className="min-h-screen bg-slate-50 flex items-center justify-center text-sm text-slate-500">Loading…</div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-6">
      <div className="w-full max-w-lg" data-testid="consumer-setup">
        <div className="text-center mb-6">
          <div className="w-12 h-12 rounded-xl bg-blue-600 mx-auto flex items-center justify-center text-white">
            <ShieldCheck className="w-6 h-6" />
          </div>
          <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-blue-700 mt-3">Secure setup link</div>
          <h1 className="font-display text-2xl font-bold text-slate-900 mt-1">Hi {info.consumer_first_name},</h1>
          <p className="text-sm text-slate-600 mt-2">
            <span className="font-semibold">{info.clinic_name}</span> has initiated the next step for your selected program.
            <br />Click below to securely continue with the service provider.
          </p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-3">
          <div className="rounded-lg bg-slate-50 border border-slate-200 px-4 py-3">
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Service provider</div>
            <div className="text-sm font-semibold text-slate-900">{info.vendor_name}</div>
            <div className="text-[11px] text-slate-500 capitalize mt-0.5">{info.program_type} program · {info.status.replace(/_/g, " ")}</div>
          </div>
          {acknowledged ? (
            <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-4 py-4 text-sm text-emerald-900 flex items-start gap-3" data-testid="consumer-setup-acknowledged">
              <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
              <div>
                <div className="font-semibold mb-1">You're all set.</div>
                {info.vendor_name} will reach out shortly with the next step. You can safely close this window.
              </div>
            </div>
          ) : (
            <>
              <p className="text-xs text-slate-500 leading-relaxed">
                {info.next_action}
              </p>
              <button
                onClick={ack}
                data-testid="consumer-setup-complete"
                className="w-full inline-flex items-center justify-center gap-2 bg-blue-600 text-white rounded-full px-4 py-3 text-sm font-semibold hover:bg-blue-700"
              >
                Complete setup <ExternalLink className="w-3.5 h-3.5" />
              </button>
            </>
          )}
        </div>
        <div className="text-center mt-4 text-[10px] text-slate-400 leading-relaxed max-w-md mx-auto">
          Nodera Health is the operational software layer that connects clinics and vendors. Nodera does not provide medical care, retain clinical records, or custody payment funds.
        </div>
      </div>
    </div>
  );
}
